import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router';
import { Link } from 'ui/links';
import { Caption } from 'ui/theme';
import { ColumnData } from 'ui/tableHeader';
import { PageActions } from './PageActions';
import { Cell, ProjectCell, ProjectName } from './UsagePages.styled';
import { useBillingQueryContext } from '../../contexts';
import {
  JOURNEY_NAME_COLUMN_LABEL,
  JOURNEY_NAME_COLUMN_VALUE,
  LABELS_BY_METRIC_NAME,
} from '../../constants';
import { SimpleIndexPage } from '../SimpleIndexPage/SimpleIndexPage';
import { Row } from '../SimpleIndexPage/types';

export const pageTitle = (projectName: string) => `Journey events: ${projectName}`;

/* this should be changed to filter by name once we add journey id */
const queryByFn = (query: string, row: Row) => {
  if (query.length === 0) return true;
  return (row[JOURNEY_NAME_COLUMN_VALUE]?.toString() || '')
    .toLowerCase()
    .startsWith(query.toLowerCase());
};

const columns = [
  {
    value: JOURNEY_NAME_COLUMN_VALUE,
    name: JOURNEY_NAME_COLUMN_LABEL,
  },
  {
    value: 'value',
    name: LABELS_BY_METRIC_NAME.JvtCount,
    align: 'center',
    sortable: true,
  },
] as ColumnData[];

export const JourneyEventsUsageByProject = () => {
  const { isLoading, pickJVTbyProjectBreakdownByJourney } = useBillingQueryContext();
  const { search } = useLocation();
  const { projectId = '' } = useParams<{ projectId: string }>();

  const data = pickJVTbyProjectBreakdownByJourney(projectId);
  const journeys = data.length > 0 ? data[0].journeys || [] : [];

  const projectName = data.length > 0 ? data[0].projectName || '' : '';

  useEffect(() => {
    window.scrollTo();
  }, []);

  const rows = journeys.map((j) => ({ ...j, key: j.journeyId }));
  /* display the projects with the highest usage first */
  rows.sort((a, b) => (b.value ? (b.value as number) : 0) - (a.value ? (a.value as number) : 0));

  return (
    <SimpleIndexPage
      rows={rows}
      columns={columns}
      createRowCell={createRow}
      breadcrumb={{
        content: 'USAGE AND BILLING',
        href: `/payments/info${search}`,
      }}
      projectsBreadcrumb={{
        content: 'ALL PROJECTS',
        href: `/payments/info/journeyEventsUsage${search}`,
      }}
      pageTitle={pageTitle(projectName)}
      pageActions={<PageActions />}
      isLoading={isLoading}
      queryByFn={queryByFn}
    />
  );
};

type CellProps = {
  isOrgRow: boolean;
  row: Row;
  colValue: string;
};

const TableCell = ({ isOrgRow, row, colValue }: CellProps) => {
  return colValue === 'journeyId' ? (
    <ProjectCell isOrg={isOrgRow}>
      <ProjectName>
        <Link
          data-test="journey-information-link"
          useReactRouter
          href={`/workflows/${row.journeyId}/edit?mode=beta&workflowType=Published`}
        >
          {row.journeyId}
        </Link>
      </ProjectName>
      <Caption>Journey ID: {row.journeyId}</Caption>
    </ProjectCell>
  ) : (
    <Cell isOrg={isOrgRow}>{row[colValue]?.toLocaleString()}</Cell>
  );
};

export const createRow =
  (colValue: string) =>
  ({ row }: { row: Record<string, string | number> }) => (
    <TableCell isOrgRow={false} row={row} colValue={colValue} />
  );
